import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { X } from "lucide-react";
import keysImage from "@/assets/keys-apartment.jpg";
import { sendToGAS, normalizePhoneFront } from "@/lib/submitToGAS";


interface AdministrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdministrationModal = ({ isOpen, onClose }: AdministrationModalProps) => {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    propertyType: "",
    comuna: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await sendToGAS({
        nombreCompleto: formData.name,
        telefono: normalizePhoneFront(formData.phone),
        correo: formData.email,
        tipoPropiedad: formData.propertyType,
        comuna: formData.comuna,
      }, { source: 'AdministraciónModal' });

      toast({
        title: "¡Gracias por contactarte con Rea Managers!",
        description: "Hemos recibido tu información y un asesor se comunicará contigo a la brevedad.",
      });

      // Reset form and close modal
      setFormData({ name: "", phone: "", email: "", propertyType: "", comuna: "" });
      setShowForm(false);
      onClose();
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      toast({
        title: "Error",
        description: "Hubo un problema al enviar el formulario. Por favor intenta nuevamente.",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary text-center mb-6">
            Líderes en Administración de Propiedades
          </DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-8 p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4">
                Administración de arriendos – Nuestro Servicio
              </h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Publicaciones:</h4>
                  <p className="text-gray-600">
                    Publicamos en los principales portales y redes sociales, destacando características, 
                    requisitos y condiciones de arriendo.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Análisis del Cliente:</h4>
                  <p className="text-gray-600">
                    Realizamos un análisis exhaustivo de las condiciones y requisitos para elegir 
                    al arrendatario idóneo.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Gestión de Administración:</h4>
                  <p className="text-gray-600">
                    Nos encargamos de todo, gestionar mantenciones por reparaciones, pagos de cuentas 
                    de servicios, gastos comunes y todo lo que esté relacionado a tu propiedad.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Resolución Legal:</h4>
                  <p className="text-gray-600">
                    Ante cualquier dificultad o inconveniente mayor, nuestro equipo legal se encargará 
                    de solucionarlo.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <img 
              src={keysImage} 
              alt="Llaves de departamento" 
              className="w-full max-w-sm rounded-lg shadow-lg mb-6"
            />
          </div>
        </div>

        <div className="px-6 pb-6">
          {!showForm ? (
            <div className="text-center">
              <Button 
                onClick={() => setShowForm(true)}
                variant="hero"
                size="lg"
                className="text-lg px-8 py-3"
              >
                Quiero administrar mi propiedad
              </Button>
            </div>
          ) : (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-primary mb-4 text-center">
                Solicitar Administración de Propiedad
              </h4>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nombre completo</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="propertyType">Tipo de propiedad</Label>
                    <Select value={formData.propertyType} onValueChange={(value) => handleInputChange("propertyType", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casa">Casa</SelectItem>
                        <SelectItem value="departamento">Departamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="comuna">Comuna</Label>
                    <Input
                      id="comuna"
                      value={formData.comuna}
                      onChange={(e) => handleInputChange("comuna", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-4 justify-center mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="hero">
                    Enviar solicitud
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};